import React, { useState, useEffect, useRef } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { useNavigate, useLocation } from 'react-router-dom';
import { getLocalDateString } from '../utils/dateUtils';
import { Loader2, CalendarDays, ChevronDown, ChevronRight, UtensilsCrossed, X, Camera, Save, TrendingUp, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { play as playSound } from '../lib/soundDirector';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { BadgeCelebration } from '../components/BadgeCelebration';
import MealReminder from '../components/MealReminder';
import PushNotificationToggle from '../components/PushNotificationToggle';
import { VitalityCard } from '../components/VitalityCard';
import BudgetHeroWidget from '../components/BudgetHeroWidget';
import FamilyProfileWidget from '../components/FamilyProfileWidget';
import BarcodeScanner from '../components/BarcodeScanner';
import FoodItemEditor from '../components/FoodItemEditor';
import DebriefExtraMiles from '../components/DebriefExtraMiles';
import MorningDebrief from '../components/MorningDebrief';
import BattlePlanTracker from '../components/BattlePlanTracker';
import DaysActiveBadge from '../components/DaysActiveBadge';
import MedicalShieldCard from '../components/MedicalShieldCard';
import PainCheckPopup from '../components/PainCheckPopup';
import FridayDebrief from '../components/FridayDebrief';
import TerrainProfile from '../components/TerrainProfile';
import TerrainOnboardingView from '../components/TerrainOnboardingView';
import FastingWidget from '../components/FastingWidget';
import PatternInsights from '../components/PatternInsights';
import { useFastingGuard, FastingCompletePopup } from '../components/FastingGuard';
import StepsWidget from '../components/StepsWidget';
import CalendlyButton from '../components/CalendlyButton';
import { useAccess } from '../hooks/useAccess';
import { useMealDraft } from '../hooks/useMealDraft';
import NextBadgeWidget from '../components/NextBadgeWidget';
import SupplementsWidget from '../components/SupplementsWidget';
import StreakShareButton from '../components/StreakShareButton';
import ChallengeNotificationBanner from '../components/ChallengeNotificationBanner';
import TacticalShiftBanner from '../components/TacticalShiftBanner';
import CoachYumixIntroModal, { hasSeenCoachYumixIntro } from '../components/CoachYumixIntroModal';
import { TacticalWeeklyChallenge } from '../components/TacticalWeeklyChallenge';
import { compressImage } from '../utils/imageCompression';
import { checkImageQuality } from '../utils/imageQuality';
import ErrorLogger from '../utils/errorLogger';
import ScanTelemetry from '../utils/scanTelemetry';
import {
  validatePhotoFile,
  compressForUpload,
  analyzeMealPhoto,
  analyzeMealPhotoStream,
  validateTextDescription,
  analyzeMealText,
  analyzeMealTextStream,
  difficultAnalysisMessage,
  mapAnalysisError,
  newIdempotencyKey,
  prewarmVision,
} from '../utils/mealPipeline';
import { checkCameraReadiness, openNativeCamera } from '../utils/cameraCapture';
import { swrLive, readCache, writeCache, invalidatePrefix } from '../utils/localCache';
import { prewarmScanModules } from '../utils/prewarm';
import PullToRefresh from '../components/PullToRefresh';
import GeolocationPrompt from '../components/GeolocationPrompt';
import { WelcomeChallengeCard, WelcomeConfetti } from '../components/WelcomeChallenge';
import Day7CelebrationModal from '../components/Day7CelebrationModal';
import Day8PivotModal from '../components/Day8PivotModal';
import Day8PriorityBlock from '../components/Day8PriorityBlock';
import DailyMindsetBanner from '../components/DailyMindsetBanner';
import PillarProgressionPopup from '../components/PillarProgressionPopup';
import WeeklyQuestCard from '../components/WeeklyQuestCard';
import OrionSymbioseModal from '../components/OrionSymbioseModal';
import ActivateCycleBanner from '../components/ActivateCycleBanner';
import { MindsetWeeklyRecapBanner, MindsetMonthlyRecapBanner } from '../components/MindsetWeeklyRecap';
import { HeroFemaleWatcher } from '../components/HeroBadges';
import InjuryCaptureDialog from '../components/InjuryCaptureDialog';
import InjuryAdaptationCard from '../components/InjuryAdaptationCard';
import { RetentionChallenges } from '../components/RetentionChallenges';
import PackChallenge from '../components/PackChallenge';
import { RecurringMealCard } from '../components/QuickMealLog';
import HealthProfileOnboardingModal from '../components/HealthProfileOnboardingModal';
import RankUpModal from '../components/RankUpModal';
import ProfileGradeUpModal from '../components/ProfileGradeUpModal';
import ClashShortcut from '../components/ClashShortcut';
import DisciplineScore from '../components/DisciplineScore';
import MealAnalysisErrorBoundary from '../components/MealAnalysisErrorBoundary';
import DraftMealSheet from '../components/DraftMealSheet';
import WorkoutOnboardingModal from '../components/WorkoutOnboardingModal';
import { useRamadanGuard, RamadanBanner } from '../components/RamadanGuard';
import { HydrationGauge } from '../components/HydrationGauge';
import { useAdaptiveUI } from '../hooks/useAdaptiveUI';
import {
  AddMealOptionsModal,
  TextMealInputModal,
  MealAnalysisModal,
  LowQualityPhotoModal,
  FavoritesModal,
  BadgesModal,
  WorkoutModal,
  LabelScannerModal,
  BarcodeResultModal
} from '../components/modals';
import { DashboardHeader } from '../components/DashboardHeader';
import LocalityWelcomeBanner from '../components/LocalityWelcomeBanner';
import ErrorBoundary from '../components/ErrorBoundary';
import HudMissionTracker from '../components/HudMissionTracker';
import PillarAlertBubble from '../components/PillarAlertBubble';
import { DashboardMission } from '../components/DashboardMission';
import { DashboardAdvancedStats } from '../components/DashboardAdvancedStats';
import { TripleVictoryWidget } from '../components/ui/MicroVictory';
import { DashboardFAB } from '../components/DashboardFAB';
// Iter. 406.e — Mission0Card import retired (photo capture moved to J7 of welcome challenge).
import ClashWidget from '../components/ClashWidget';

export const Dashboard = () => {
  const { user, updateUser } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { isEpure, isJunior, adaptLabel } = useAdaptiveUI();
  const { access } = useAccess();
  const [stats, setStats] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [badges, setBadges] = useState({ earned: [], progress: [] });
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [injuryDialogOpen, setInjuryDialogOpen] = useState(false);
  const [showBadges, setShowBadges] = useState(false);
  const [showWorkout, setShowWorkout] = useState(false);
  // Iter. 405.h — J5 onboarding wizard. Opens when the user clicks the
  // J5 "Démarrer" button on the welcome challenge widget. The wizard
  // calls /api/workouts/generate with `onboarding_mode=true`, persists
  // the plan in db.ai_workout_plans (14-day trial pipeline already
  // gated by services/access_control.require_active_access), and then
  // navigates the user to /workouts to track their session set by set.
  const [showWorkoutOnboarding, setShowWorkoutOnboarding] = useState(false);
  const [workoutLoading, setWorkoutLoading] = useState(false);
  const [celebrationBadge, setCelebrationBadge] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [optimizingImage, setOptimizingImage] = useState(false);
  // Iter. 404 · Lot #13 — Live phase emitted by the streaming endpoint.
  // Drives the dynamic step label in MealAnalysisModal so the user sees
  // *real* backend progress (received → llm_start → llm_progress …) instead
  // of a timer-based carousel.
  const [streamPhase, setStreamPhase] = useState(null);
  const [streamLLMElapsedMs, setStreamLLMElapsedMs] = useState(0);
  // Iter. 404.e — Soft escape hatch after 45s loader stall. We call this
  // from the modal "Annuler" button. Closes the modal cleanly so the user
  // can retry; the in-flight fetch eventually aborts on its own timeout.
  const handleAbortAnalysis = React.useCallback(() => {
    try { ScanTelemetry.trackEvent('scan_user_aborted', { context: 'loader_stuck_45s' }); } catch { /* noop */ }
    setAnalyzing(false);
    setOptimizingImage(false);
    setShowUpload(false);
    setAnalysisResult(null);
    setStreamPhase(null);
    toast.info(
      language === 'fr'
        ? "Analyse annulée. Tu peux réessayer ou décrire ton repas à la place."
        : 'Analysis cancelled. Retry or describe your meal instead.',
      { duration: 5000 }
    );
  }, [language]);
  const [analysisResult, setAnalysisResult] = useState(null);
  // Low-quality photo gate — shown BEFORE sending to Vision AI when image is dark/blurry
  const [qualityCheck, setQualityCheck] = useState(null); // { quality, base64 } | null
  const [workoutForm, setWorkoutForm] = useState({
    workout_type: 'musculation',
    intensity: 'moderate',
    duration_minutes: 45,
    exercise_name: '',
    weight_lbs: '',
    reps: '',
    notes: '',
    // Iter. 406.t — Rattrapage: in-modal date picker. Default null → "today".
    target_date: null,
  });
  const [showTextInput, setShowTextInput] = useState(false);
  const [textMealForm, setTextMealForm] = useState({ description: '', meal_type: 'repas', portions: 1 });
  const [showAddMealOptions, setShowAddMealOptions] = useState(false);
  const [photoPortions, setPhotoPortions] = useState(1);
  const [showTerrainOnboarding, setShowTerrainOnboarding] = useState(false);

  // Iter. 406.n — Orion Symbiose celebration when the user completes 7/7 quests.
  const [orionCelebration, setOrionCelebration] = useState(null);
  // Iter. 406.p — When the WELCOME challenge wraps 7/7, surface the Orion
  // modal with a "Concevoir mon plan 6 semaines" CTA. This is a one-shot
  // local trigger (parent state) — sessionStorage prevents re-fire on refresh.
  const [welcomeOrionOpen, setWelcomeOrionOpen] = useState(false);
  // Iter. 406.r — Track welcome-challenge active state for the priority-glow
  // wrapper around the WelcomeChallengeCard. Glow disappears once complete.
  const [welcomeActive, setWelcomeActive] = useState(false);
  // Iter. 406.fr.11 — Coach YUMIX epic intro cinematic, one-shot per user.
  const [showCoachIntro, setShowCoachIntro] = useState(false);
  useEffect(() => {
    if (!user?.user_id) return;
    // Defer slightly so the dashboard skeleton paints first.
    const t = setTimeout(() => {
      if (!hasSeenCoachYumixIntro()) setShowCoachIntro(true);
    }, 800);
    return () => clearTimeout(t);
  }, [user?.user_id]);
  const fetchOrionCelebration = React.useCallback(async () => {
    try {
      const res = await api.get('/weekly-quest/pending-celebration');
      if (res?.data?.celebration) setOrionCelebration(res.data.celebration);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (!user?.user_id) return;
    fetchOrionCelebration();
    // Iter. 406.p — check if welcome challenge JUST hit 7/7 + no cycle yet
    (async () => {
      try {
        const [stateRes, statusRes] = await Promise.all([
          api.get('/onboarding/state'),
          api.get('/welcome-challenge/status'),
        ]);
        const s = stateRes?.data || {};
        const stat = statusRes?.data || {};
        // Iter. 406.r — Track whether the welcome-challenge is still active
        // (not yet 7/7 complete). Used to gate the priority-glow wrapper.
        setWelcomeActive(!!stat.active && !s.welcome_completed);
        const seenKey = `yumix_welcome_orion_seen_${user.user_id}`;
        const alreadySeen = (() => {
          try { return !!sessionStorage.getItem(seenKey); } catch { return false; }
        })();
        if (
          s.welcome_completed
          && !s.has_active_cycle
          && stat.active // welcome doc still exists
          && !alreadySeen
        ) {
          setWelcomeOrionOpen(true);
          try { sessionStorage.setItem(seenKey, '1'); } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    })();
    // Iter. 406.p — Auto-open the wizard when arriving via the permanent
    // nav entry "Activer mon cycle 6 semaines" (?activate-cycle=1).
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('activate-cycle') === '1') {
        api.post('/onboarding/restore-plan-cta').catch(() => {});
        setShowWorkoutOnboarding(true);
        const cleaned = window.location.pathname;
        window.history.replaceState({}, '', cleaned);
      }
    } catch { /* ignore */ }
  }, [user?.user_id, fetchOrionCelebration]);

  // Auto-open meal add modal when navigated from GlobalFAB or Mes Repas.
  // Phase 1 retroactive: when called from "Rattraper un repas" button on the
  // Meals page, an optional `prefillDate` (YYYY-MM-DD) is passed in the
  // navigation state to pre-select that date in the meal-add modal.
  useEffect(() => {
    if (location.state?.openMealAdd) {
      const prefillDate = location.state.prefillDate;
      if (prefillDate && typeof prefillDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(prefillDate)) {
        setSelectedDate(prefillDate);
      }
      setShowAddMealOptions(true);
      prewarmVision();
      // Clear the state so it doesn't re-trigger on back navigation
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  // Assistant IA deep-link: /dashboard?action=describe&text=... or photo/favorites/label
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const action = sp.get('action');
    if (!action) return;
    if (action === 'describe') {
      const text = (sp.get('text') || '').slice(0, 500);
      setTextMealForm(prev => ({ ...prev, description: text }));
      setShowTextInput(true);
      prewarmVision();
    } else if (action === 'photo') {
      setShowAddMealOptions(true);
      prewarmVision();
    } else if (action === 'favorites') {
      setShowAddMealOptions(true);
      // openFavorites will be triggered by the modal
    } else if (action === 'label') {
      setShowAddMealOptions(true);
      prewarmVision();
    }
    // Clean URL
    const next = new URLSearchParams(sp);
    ['action', 'text', 'meal_type', 'portions'].forEach(k => next.delete(k));
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // First-time user: redirect to Assistant IA for the onboarding briefing.
  // Triggered once per user (localStorage flag). Set by CoachAI briefing effect.
  useEffect(() => {
    if (!user?.user_id) return;
    if (user?.role && user.role !== 'client') return;
    if (!user?.onboarding_complete) return;
    try {
      const flag = `yumix_ai_briefing_seen_${user.user_id}`;
      if (!localStorage.getItem(flag)) {
        navigate('/coach-ai', { replace: true });
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, user?.onboarding_complete]);
  const [isEditingNutrition, setIsEditingNutrition] = useState(false);
  const [editedNutrition, setEditedNutrition] = useState({});
  const [savingNutrition, setSavingNutrition] = useState(false);
  const [showLabelScanner, setShowLabelScanner] = useState(false);
  const [labelResult, setLabelResult] = useState(null);
  const [labelServings, setLabelServings] = useState(1);
  const [labelProductName, setLabelProductName] = useState('');
  const [scanningLabel, setScanningLabel] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [barcodeResult, setBarcodeResult] = useState(null);
  const [barcodeServings, setBarcodeServings] = useState(1);
  const [lookingUpBarcode, setLookingUpBarcode] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [todayLogCount, setTodayLogCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [incompleteDays, setIncompleteDays] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [addingFavorite, setAddingFavorite] = useState(false);
  const [mealToDelete, setMealToDelete] = useState(null);
  const [deletingMeal, setDeletingMeal] = useState(false);
  const [clientPhoto, setClientPhoto] = useState(null);
  const [editableFoods, setEditableFoods] = useState([]);
  const [vitality, setVitality] = useState(null);
  const [clinicalMode, setClinicalMode] = useState(null);
  const [tripleStats, setTripleStats] = useState(null);
  const [photoMealType, setPhotoMealType] = useState('repas');
  const [barcodeMealType, setBarcodeMealType] = useState('repas');
  // DRAFT MEAL — Multi-ingredient cart (Iter. 396 Lot #12). Centralized in
  // a hook with localStorage persistence so users don't lose ingredients
  // when the camera times out or they switch between scan/describe modes.
  const {
    items: draftMealItems,
    addItems: addDraftMealItems,
    removeItem: removeDraftMealItem,
    clear: clearDraftMeal,
    wasRestored: draftWasRestored,
    ackRestored: ackDraftRestored,
  } = useMealDraft();
  const [showDraftMeal, setShowDraftMeal] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  // Iter. 396 · Lot #12 — Restore-draft toast.
  // If a previous session left meal-cart ingredients persisted in
  // localStorage (camera timeout, accidental close, etc.), surface a
  // friendly toast on first mount with a "Repartir à zéro" action. Also
  // auto-reopens the DraftMeal sheet so the user IMMEDIATELY sees their
  // ingredients are back — no hunting required.
  const draftRestoreHandledRef = useRef(false);
  useEffect(() => {
    if (draftRestoreHandledRef.current) return;
    if (!draftWasRestored || draftMealItems.length === 0) return;
    draftRestoreHandledRef.current = true;
    const count = draftMealItems.length;
    setShowDraftMeal(true);
    toast.info(
      language === 'fr'
        ? `📝 Repas en cours restauré (${count} ingrédient${count > 1 ? 's' : ''})`
        : language === 'es'
        ? `📝 Comida en curso restaurada (${count} ingrediente${count > 1 ? 's' : ''})`
        : `📝 Meal in progress restored (${count} ingredient${count > 1 ? 's' : ''})`,
      {
        id: 'meal-draft-restored',
        duration: 8000,
        action: {
          label: language === 'fr' ? 'Repartir à zéro' : language === 'es' ? 'Reiniciar' : 'Start over',
          onClick: () => {
            clearDraftMeal();
            setShowDraftMeal(false);
            toast.success(
              language === 'fr' ? '🗑️ Panier vidé'
                : language === 'es' ? '🗑️ Carrito vaciado'
                : '🗑️ Cart cleared',
              { duration: 2500 }
            );
          },
        },
      }
    );
    ackDraftRestored();
  }, [draftWasRestored, draftMealItems.length, language, clearDraftMeal, ackDraftRestored]);
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const labelInputRef = useRef(null);
  const { checkFasting, RamadanModal } = useRamadanGuard();
  const { checkFastingGuard, FastingGuardModal } = useFastingGuard();
  const [showWelcomeConfetti, setShowWelcomeConfetti] = useState(false);
  
  // Health Profile Onboarding — trigger for veterans missing the new onboarding step
  // Iter. 406.fr.59 : we now write an empty health_profile at the end of the
  // onboarding funnel (with completed_at), so this modal only opens for legacy
  // users who registered BEFORE the health step existed.
  const [showHealthOnboarding, setShowHealthOnboarding] = useState(false);
  useEffect(() => {
    if (!user) return;
    const hp = user.health_profile;
    // Show modal only if health_profile is truly absent (legacy veteran).
    // Any object (even empty arrays) counts as "user acknowledged this step".
    if (hp === null || hp === undefined) {
      setShowHealthOnboarding(true);
    }
  }, [user]);

  // Check for welcome challenge confetti on first mount
  useEffect(() => {
    if (!user) return;
    const confettiKey = `welcome_confetti_shown_${user.user_id}`;
    if (localStorage.getItem(confettiKey)) return;

    api.get('/welcome-challenge/status').then(res => {
      if (res.data.active && res.data.challenge) {
        const completed = res.data.challenge.days_completed?.length || 0;
        // Show confetti only if early in the challenge (day 1-2, no missions done yet)
        if (completed === 0) {
          setShowWelcomeConfetti(true);
          localStorage.setItem(confettiKey, 'true');
        }
      }
    }).catch(() => {});
  }, [user]);

  // ── Opération Vitesse: hydrate from localStorage SYNCHRONOUSLY on mount ──
  // The dashboard renders instantly from cache while the background fetch
  // updates the UI once fresh data lands. First paint latency: ~0 ms.
  useEffect(() => {
    if (!user) return;
    const uid = user.user_id;
    const cachedStats = readCache(`dash:stats:${uid}`);
    const cachedWeekly = readCache(`dash:weekly:${uid}`);
    const cachedBadges = readCache(`dash:badges:${uid}`);
    const cachedVitality = readCache(`dash:vitality:${uid}`);
    const cachedTriple = readCache(`dash:triple:${uid}`);
    const cachedPhoto = readCache(`dash:clientphoto:${uid}`);
    if (cachedStats) setStats(cachedStats);
    if (cachedWeekly) setWeeklyData(cachedWeekly);
    if (cachedBadges) setBadges(cachedBadges);
    if (cachedVitality) setVitality(cachedVitality);
    if (cachedTriple) setTripleStats(cachedTriple);
    if (cachedPhoto) setClientPhoto(cachedPhoto);
  // Only run once the user object is first available
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  useEffect(() => {
    if (user) {
      fetchDashboardData();

      // Defer every non-critical side-quest until the browser is idle so the
      // first Dashboard paint is not stalled by 7+ concurrent requests.
      const idle = (cb) => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(cb, { timeout: 1200 });
        else setTimeout(cb, 150);
      };
      idle(() => {
        // Opération Vitesse: prewarm scan modules (compression, barcode, camera)
        // so the first upload after landing on the Dashboard is instant.
        prewarmScanModules();
        const uid = user.user_id;
        swrLive(
          `dash:clientphoto:${uid}`,
          () => api.get(`/client-photos/${uid}`).then(r => r.data.photo_url || null),
          (url) => { if (url) setClientPhoto(url); },
        );
        swrLive(
          `dash:vitality:${uid}`,
          () => api.get('/dashboard/vitality').then(r => r.data),
          (data) => setVitality(data),
        );
        swrLive(
          `dash:clinical:${uid}`,
          () => api.get('/users/clinical-mode').then(r => r.data.clinical_mode),
          (mode) => setClinicalMode(mode),
        );
        swrLive(
          `dash:triple:${uid}`,
          () => api.get('/leaderboard/my-stats').then(r => r.data),
          (data) => setTripleStats(data),
        );
        swrLive(
          `dash:incomplete:${uid}`,
          () => api.get('/dashboard/incomplete-days').then(r => r.data?.incomplete_days || []),
          (days) => setIncompleteDays(days || []),
          { maxAgeMs: 60 * 60 * 1000 }, // 1 h — changes rarely
        );
        // Pending survivant badge celebration — fire-and-forget, no cache
        api.get('/injuries/survivant-celebration').then(res => {
          if (res.data.pending) {
            const { tier, count, tier_label: tierLabel } = res.data;
            setCelebrationBadge({
              name: `SURVIVANT ${count > 1 ? 'x' + count : ''}`,
              description: language === 'fr'
                ? `Promotion au niveau ${tierLabel} ! Tu as persévéré à travers ${count} blessure${count > 1 ? 's' : ''}. Un vrai champion.`
                : `Promoted to ${tierLabel} tier! You persevered through ${count} injur${count > 1 ? 'ies' : 'y'}. A true champion.`,
              icon: '🛡️',
              rarity: 'legendary',
              survivantTier: tier,
              survivantCount: count,
            });
            api.post('/injuries/survivant-celebration/ack').catch(() => {});
          }
        }).catch(() => {});
      });
    }
  }, [user]);

  useEffect(() => { if (user) fetchDashboardData(); }, [selectedDate]);

  const fetchDashboardData = async () => {
    if (!user) return;
    const uid = user.user_id;
    const dateParam = selectedDate ? `?target_date=${selectedDate}` : '';
    // Hydrate from cache first so the UI is interactive immediately
    const _quickStats = readCache(`dash:stats:${uid}${selectedDate ? ':' + selectedDate : ''}`);
    if (_quickStats) setStats(_quickStats);
    try {
      const [statsRes, weeklyRes, badgesRes, badgesProgressRes, hydRes, suppRes] = await Promise.all([
        api.get(`/dashboard/stats${dateParam}`).catch(() => ({ data: null })),
        api.get('/dashboard/weekly-macros').catch(() => ({ data: null })),
        api.get('/badges').catch(() => ({ data: { badges: [], total_earned: 0, total_available: 0 } })),
        api.get('/badges/progress').catch(() => ({ data: { progress: [] } })),
        api.get('/hydration/today').catch(() => ({ data: { glasses: 0 } })),
        api.get('/supplements/today').catch(() => ({ data: { taken_ids: [] } })),
      ]);
      if (statsRes.data) {
        setStats(statsRes.data);
        const k = `dash:stats:${uid}${selectedDate ? ':' + selectedDate : ''}`;
        writeCache(k, statsRes.data);
      }
      if (weeklyRes.data) {
        setWeeklyData(weeklyRes.data);
        writeCache(`dash:weekly:${uid}`, weeklyRes.data);
      }
      // Merge /badges and /badges/progress into the shape DashboardAdvancedStats expects.
      // Prevents the "27/10" ratio bug and the empty-state message showing when badges exist.
      const mergedBadges = {
        earned: badgesRes.data?.badges || [],
        total_earned: badgesRes.data?.total_earned || 0,
        total_available: badgesRes.data?.total_available || 0,
        progress: badgesProgressRes.data?.progress || [],
      };
      setBadges(mergedBadges);
      writeCache(`dash:badges:${uid}`, mergedBadges);
      const meals = statsRes.data?.today_meals || 0;
      const workout = statsRes.data?.has_workout_today ? 1 : 0;
      const water = Math.min(hydRes.data?.glasses_drunk || hydRes.data?.glasses || 0, 1);
      const supps = (suppRes.data?.taken_ids?.length || 0) > 0 ? 1 : 0;
      setTodayLogCount(meals + workout + water + supps);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ==================== MEAL HANDLERS (refactored — uses mealPipeline) ====================
  /**
   * Photo flow — ultra-light orchestrator. All heavy lifting is in `utils/mealPipeline.js`.
   *   1. validate metadata (size, MIME, HEIC)
   *   2. open modal in "optimizing" state for instant feedback
   *   3. compress < 500 KB (single source of truth) + GC tick
   *   4. quality gate (non-blocking)
   *   5. call API → render result
   *
   * Fasting / Ramadan checks are NEVER pre-flight — they only run after the
   * analysis is secured (see `applyPostAnalysisFastingHook` below).
   */
  const handleFileSelect = async (e) => {
    const input = e.target;
    const file = input?.files?.[0];
    const resetInput = () => { try { input.value = ''; } catch { /* readonly in some engines */ } };
    if (!file) { resetInput(); return; }

    // --- 1. Synchronous validation -------------------------------------
    try {
      validatePhotoFile(file, language);
    } catch (err) {
      const msg = err.userMessage
        || (t('toast.image_invalid'));
      toast.error(msg, { duration: 8000 });
      resetInput();
      return;
    }

    // --- 2. Open modal IMMEDIATELY (before CPU-heavy compression) ------
    setAnalysisResult(null);
    setAnalyzing(true);
    setOptimizingImage(true);
    setShowUpload(true);

    // --- 3. Compress + GC tick -----------------------------------------
    let base64;
    try {
      const result = await compressForUpload(file);
      base64 = result.base64;
    } catch (err) {
      ErrorLogger.log(err, 'Meal_Photo_Compression', { severity: 'CRITICAL', file_size: file?.size, file_type: file?.type });
      setAnalyzing(false); setOptimizingImage(false); setShowUpload(false); setAnalysisResult(null);
      resetInput();
      toast.error(t('toast.image_read_error'), { duration: 6000 });
      return;
    }
    setOptimizingImage(false);
    resetInput();
    try { e.target = null; } catch { /* readonly in some engines */ }

    // --- 4. Photo quality gate (non-blocking) --------------------------
    try {
      const quality = await checkImageQuality(base64);
      if (quality && (quality.isDark || quality.isBlurry)) {
        // ⚡ Show a TOAST in addition to the modal so the user always has
        // a visible cue, even if the modal is z-indexed under something
        // else on their device (reported by Cathy who got "rien ne se passe").
        toast.warning(
          quality.isDark
            ? (language === 'fr' ? 'Photo un peu sombre — vérifie dans le pop-up' : 'Photo a bit dark — check the popup')
            : (language === 'fr' ? 'Photo un peu floue — vérifie dans le pop-up' : 'Photo a bit blurry — check the popup'),
          { duration: 5000 },
        );
        setAnalyzing(false);
        setShowUpload(false);
        setQualityCheck({ quality, base64 });
        return;
      }
    } catch { /* gate failure is silent — proceed with analysis */ }

    // --- 5. Send to API ------------------------------------------------
    await runPhotoAnalysis(base64);
  };

  /**
   * Send the compressed base64 to the backend and render the result.
   * Watchdog 130s (90s API + 18s by-idempotency recovery + buffer), abortable,
   * breadcrumb-tagged. Fasting checks are deferred until AFTER the result is
   * in hand (see Yuri's "isolation du jeûne" directive).
   *
   * `idempotencyKey` is generated ONCE per scan attempt and reused on any
   * internal retry, so a network-truncated response can never produce a
   * duplicate meal in DB nor double-charge OpenAI.
   */
  const runPhotoAnalysis = async (imageBase64, idempotencyKey = null) => {
    setAnalyzing(true);
    setShowUpload(true);
    const idemKey = idempotencyKey || newIdempotencyKey();
    // 🛒 Iter. 405.d (Léonie race-condition fix) — Decide UPFRONT whether
    // this analysis must run in cart-mode. If the user already has draft
    // items OR has the draft sheet open, the backend must NOT persist a
    // meal: the consolidated payload is sent via /meals/from-label when
    // the user clicks the single "Enregistrer" button. This eliminates
    // the "two separate meals" bug where the backend persisted on every
    // analyze while the frontend was still composing a hybrid meal.
    const cartModeAtStart = draftMealItems.length > 0 || showDraftMeal;
    const watchdog = setTimeout(() => {
      console.warn('[SCAN] ⏱️ Watchdog (130s) — déblocage UI forcé');
      setAnalyzing(false);
      setShowUpload(false);
      toast.error(difficultAnalysisMessage(language));
    }, 130000);

    try {
      // Iter. 404 · Lot #13 — Try the streaming endpoint first. Graceful
      // fall-back to the legacy /meals/analyze if the browser doesn't
      // support fetch ReadableStream or the stream errors mid-flight.
      let safeData;
      setStreamPhase('received');
      setStreamLLMElapsedMs(0);
      const handlePhase = (phase, evt) => {
        if (!phase) return;
        setStreamPhase(phase);
        if (phase === 'llm_progress' && typeof evt?.elapsed_ms === 'number') {
          setStreamLLMElapsedMs(evt.elapsed_ms);
        }
        if (phase === 'llm_done' && typeof evt?.elapsed_ms === 'number') {
          setStreamLLMElapsedMs(evt.elapsed_ms);
        }
      };
      try {
        safeData = await analyzeMealPhotoStream({
          base64: imageBase64,
          mealType: photoMealType,
          portions: photoPortions || 1,
          targetDate: selectedDate || undefined,
          ramadanOverride: false,
          idempotencyKey: idemKey,
          onPhase: handlePhase,
          dryRun: cartModeAtStart,
        });
      } catch (streamErr) {
        // Streaming-specific failures → silent fallback to legacy endpoint.
        const looksLikeStreamFailure =
          streamErr?.message === 'STREAM_NOT_SUPPORTED' ||
          streamErr?.message === 'STREAM_ENDED_WITHOUT_COMPLETE' ||
          streamErr?.name === 'TypeError'; // fetch network error
        if (!looksLikeStreamFailure) throw streamErr;
        console.warn('[SCAN/STREAM] Fallback to legacy endpoint:', streamErr?.message);
        safeData = await analyzeMealPhoto({
          base64: imageBase64,
          mealType: photoMealType,
          portions: photoPortions || 1,
          targetDate: selectedDate || undefined,
          ramadanOverride: false,
          idempotencyKey: idemKey,
          onSlowFeedback: (attempt) => {
            toast.info(
              language === 'fr'
                ? (attempt > 0 ? '🔄 Nouvelle tentative en cours…' : '🔬 Analyse des nutriments en cours…')
                : (attempt > 0 ? '🔄 Retrying…' : '🔬 Analyzing nutrients…'),
              { id: 'meal-slow-feedback', duration: 4500 }
            );
          },
        });
      } finally {
        setStreamPhase(null);
      }
      ErrorLogger.clearBreadcrumbs();

      // Honor backend cultural-block flags (these come from the API itself,
      // not from a pre-flight check, so they're already "post-analysis").
      if (safeData.ramadan_block) {
        setAnalyzing(false); setShowUpload(false);
        toast.info(safeData.message, { duration: 5000 });
        return;
      }
      if (safeData.cultural_block) {
        setAnalyzing(false); setShowUpload(false);
        toast.error(safeData.yuri_alert, { duration: 8000 });
        return;
      }

      // 🔥 Render result instantly — no awaits between flip + setResult.
      const photoFoodsCount = safeData.analysis?.foods_detected?.length || 0;
      // Iter. 396 · Lot #12 — UNIFIED CART (photo flavor).
      // Iter. 405.d — Léonie race-condition: a `dry_run:true` response
      // means the backend persisted NOTHING; ALWAYS merge into the cart
      // so the consolidated meal is the only persisted record (via the
      // single "Enregistrer" button → /meals/from-label).
      // If we ran in legacy mode but the cart became non-empty WHILE we
      // were analysing (rare race), delete the just-created meal_id and
      // merge into the cart anyway — preserves the single-payload contract.
      const responseIsDryRun = !!safeData.dry_run;
      const cartHasItemsNow = draftMealItems.length > 0;
      const shouldMergeIntoCart = (responseIsDryRun || cartHasItemsNow) && photoFoodsCount > 0;
      if (!responseIsDryRun && cartHasItemsNow && safeData.meal_id) {
        // Compensating delete — the backend already persisted but the
        // user has a hybrid cart in progress. We MUST NOT leave a stray
        // meal behind. Fire-and-forget so we don't block the merge UX.
        try {
          api.delete(`/meals/${safeData.meal_id}`).catch((_e) => {});
          console.warn('[SCAN] race: meal persisted then cart became non-empty → compensating delete', safeData.meal_id);
        } catch (_e) { /* swallow */ }
      }
      if (shouldMergeIntoCart) {
        const newItems = safeData.analysis.foods_detected.map(f => ({
          name: f.name,
          portion_g: f.portion_g || 100,
          protein_g: f.protein_g || 0,
          calories: f.calories || 0,
          carbs_g: f.carbs_g || 0,
          fats_g: f.fats_g || 0,
          fiber_g: f.fiber_g || 0,
          source: 'photo',
        }));
        addDraftMealItems(newItems);
        setAnalysisResult(null);
        setShowUpload(false);
        setShowDraftMeal(true);
        setAnalyzing(false);
        toast.success(
          language === 'fr'
            ? `🛒 ${photoFoodsCount} ingrédient${photoFoodsCount > 1 ? 's' : ''} ajouté${photoFoodsCount > 1 ? 's' : ''} au panier`
            : language === 'es'
            ? `🛒 ${photoFoodsCount} ingrediente${photoFoodsCount > 1 ? 's' : ''} añadido${photoFoodsCount > 1 ? 's' : ''} al carrito`
            : `🛒 ${photoFoodsCount} ingredient${photoFoodsCount > 1 ? 's' : ''} added to cart`,
          { duration: 4500 }
        );
      } else {
        setAnalysisResult(safeData);
        if (safeData.analysis?.foods_detected) setEditableFoods(initializeEditableFoods(safeData.analysis.foods_detected));
        setAnalyzing(false);
        toast.success(safeData.message || t('toast.meal_analyzed'), { duration: 8000, closeButton: true });
      }

      // Fire-and-forget — badges + refresh, never block the result modal.
      (async () => {
        try {
          const badgesResponse = await api.post('/badges/check');
          if (badgesResponse.data.newly_earned?.length > 0) {
            const rare = ['rare', 'epic', 'legendary'];
            const bc = badgesResponse.data.newly_earned.find(b => rare.includes(b.rarity));
            if (bc) setCelebrationBadge(bc);
            else toast.success(`${badgesResponse.data.newly_earned[0].icon} ${language === 'fr' ? 'Nouveau badge:' : 'New badge:'} ${badgesResponse.data.newly_earned[0].name}!`, { duration: 5000 });
          }
        } catch { /* non-critical */ }
        fetchDashboardData();
      })();
    } catch (error) {
      const { msg, status, isTimeout, isPaywall } = mapAnalysisError(error, language, 'photo');
      // 🚦 Paywall (402) — GlobalPaywallListener opens the PaywallModal,
      // but we ALSO toast so the user has a visible cue even if the modal
      // is stacked behind something on their device (Cathy F. case).
      if (isPaywall) {
        toast.error(
          language === 'fr'
            ? 'Essai gratuit terminé — passe au coaching pour continuer'
            : 'Free trial ended — upgrade to keep scanning',
          { duration: 6000 },
        );
        setAnalyzing(false);
        setShowUpload(false);
        setAnalysisResult(null);
        return;
      }
      console.error('[SCAN] ❌', { status, isTimeout, error });
      ErrorLogger.log(error, 'Meal_Analysis_Photo', { severity: 'CRITICAL', stage: 'api_call', status, is_timeout: isTimeout });
      toast.error(msg, { duration: 7000 });
      setAnalyzing(false);
      setShowUpload(false);
    } finally {
      clearTimeout(watchdog);
    }
  };

  const logWorkout = async () => {
    setWorkoutLoading(true);
    try {
      const payload = { workout_type: workoutForm.workout_type, intensity: workoutForm.intensity, duration_minutes: workoutForm.duration_minutes };
      if (workoutForm.exercise_name) payload.exercise_name = workoutForm.exercise_name;
      if (workoutForm.weight_lbs) payload.weight_lbs = parseFloat(workoutForm.weight_lbs);
      if (workoutForm.reps) payload.reps = parseInt(workoutForm.reps);
      if (workoutForm.notes) payload.notes = workoutForm.notes;
      // Iter. 406.t — Tony's Rattrapage bug fix: prioritize the in-modal date
      // picker; fall back to the dashboard-level historical selection.
      const todayStr = getLocalDateString();
      const chosenDate = workoutForm.target_date || selectedDate;
      if (chosenDate && chosenDate !== todayStr) payload.target_date = chosenDate;
      const response = await api.post('/workouts/log', payload);
      toast.success(response.data.message, { duration: 6000 });
      if (response.data.workout?.is_pr) setTimeout(() => toast.success(`PR! ${response.data.workout.pr_details?.exercise} ${response.data.workout.pr_details?.new_weight} lbs`, { duration: 5000 }), 1500);
      setShowWorkout(false);
      api.get('/leaderboard/my-stats').then(res => setTripleStats(res.data)).catch(() => {});
      fetchDashboardData();
      api.post('/calendar/sync-workout', payload).catch(() => {});
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toast.workout_log_error'));
    } finally { setWorkoutLoading(false); }
  };

  const deleteWorkout = async () => {
    try { const response = await api.delete('/workouts/today'); toast.success(response.data.message); fetchDashboardData(); } catch (error) { console.error('Workout delete error:', error); }
  };

  /**
   * Text analysis — same robustness as photo flow.
   * Watchdog 130s (90s API + 18s by-idempotency recovery + buffer),
   * fasting checks deferred (handled by backend response flags).
   */
  const analyzeTextMeal = async () => {
    try {
      validateTextDescription(textMealForm.description);
    } catch {
      toast.error(t('toast.describe_meal_detail'));
      return;
    }
    setAnalyzing(true);
    setShowTextInput(false);
    setShowUpload(true);
    const idemKey = newIdempotencyKey();
    // 🛒 Iter. 405.d — Cart-mode dry_run decision (see runPhotoAnalysis).
    const cartModeAtStart = draftMealItems.length > 0 || showDraftMeal;
    const watchdog = setTimeout(() => {
      console.warn('[TEXT] ⏱️ Watchdog (130s) — déblocage UI forcé');
      setAnalyzing(false);
      setShowUpload(false);
      toast.error(difficultAnalysisMessage(language));
    }, 130000);

    try {
      // Iter. 404.c — Try the streaming text endpoint first. Graceful
      // fall-back to the legacy /meals/analyze-text if streaming fails.
      let safeData;
      setStreamPhase('received');
      setStreamLLMElapsedMs(0);
      const handlePhase = (phase, evt) => {
        if (!phase) return;
        setStreamPhase(phase);
        if ((phase === 'llm_progress' || phase === 'llm_done') && typeof evt?.elapsed_ms === 'number') {
          setStreamLLMElapsedMs(evt.elapsed_ms);
        }
      };
      try {
        safeData = await analyzeMealTextStream({
          description: textMealForm.description,
          mealType: textMealForm.meal_type || 'repas',
          portions: textMealForm.portions || 1,
          targetDate: selectedDate || undefined,
          ramadanOverride: false,
          idempotencyKey: idemKey,
          onPhase: handlePhase,
          dryRun: cartModeAtStart,
        });
      } catch (streamErr) {
        const looksLikeStreamFailure =
          streamErr?.message === 'STREAM_NOT_SUPPORTED' ||
          streamErr?.message === 'STREAM_ENDED_WITHOUT_COMPLETE' ||
          streamErr?.name === 'TypeError';
        if (!looksLikeStreamFailure) throw streamErr;
        console.warn('[TEXT/STREAM] Fallback to legacy endpoint:', streamErr?.message);
        safeData = await analyzeMealText({
          description: textMealForm.description,
          mealType: textMealForm.meal_type || 'repas',
          portions: textMealForm.portions || 1,
          targetDate: selectedDate || undefined,
          ramadanOverride: false,
          idempotencyKey: idemKey,
          onSlowFeedback: (attempt) => {
            toast.info(
              language === 'fr'
                ? (attempt > 0 ? '🔄 Nouvelle tentative en cours…' : '🔬 Analyse des nutriments en cours…')
                : (attempt > 0 ? '🔄 Retrying…' : '🔬 Analyzing nutrients…'),
              { id: 'meal-slow-feedback', duration: 4500 }
            );
          },
        });
      } finally {
        setStreamPhase(null);
      }
      ErrorLogger.clearBreadcrumbs();

      if (safeData.ramadan_block || safeData.cultural_block) {
        setAnalyzing(false); setShowUpload(false);
        toast.error(safeData.yuri_alert || safeData.message, { duration: 8000 });
        return;
      }

      if (safeData.analysis?.foods_detected) setEditableFoods(initializeEditableFoods(safeData.analysis.foods_detected));
      setAnalysisResult(safeData);
      const foodsCount = safeData.analysis?.foods_detected?.length || 0;
      // ──────────────────────────────────────────────────────────────
      // Iter. 396 · Lot #12 — UNIFIED CART
      // Iter. 405.d — Léonie race-condition fix. `dry_run:true` means the
      // backend persisted nothing → ALWAYS merge into cart. Race fallback:
      // if a legacy non-dry-run response arrives but the cart has been
      // populated meanwhile, compensate by deleting the just-persisted
      // meal so we keep the "one consolidated payload" invariant.
      // ──────────────────────────────────────────────────────────────
      const responseIsDryRun = !!safeData.dry_run;
      const cartHasItemsNow = draftMealItems.length > 0;
      const shouldMergeIntoCart = (responseIsDryRun || cartHasItemsNow) && foodsCount > 0;
      if (!responseIsDryRun && cartHasItemsNow && safeData.meal_id) {
        try {
          api.delete(`/meals/${safeData.meal_id}`).catch((_e) => {});
          console.warn('[TEXT] race: meal persisted then cart became non-empty → compensating delete', safeData.meal_id);
        } catch (_e) { /* swallow */ }
      }
      if (shouldMergeIntoCart) {
        const newItems = safeData.analysis.foods_detected.map(f => ({
          name: f.name,
          portion_g: f.portion_g || 100,
          protein_g: f.protein_g || 0,
          calories: f.calories || 0,
          carbs_g: f.carbs_g || 0,
          fats_g: f.fats_g || 0,
          fiber_g: f.fiber_g || 0,
          source: 'text',
        }));
        addDraftMealItems(newItems);
        setAnalysisResult(null);
        setShowUpload(false);
        setShowDraftMeal(true);
        setAnalyzing(false);
        toast.success(
          language === 'fr'
            ? `🛒 ${foodsCount} ingrédient${foodsCount > 1 ? 's' : ''} ajouté${foodsCount > 1 ? 's' : ''} au panier`
            : language === 'es'
            ? `🛒 ${foodsCount} ingrediente${foodsCount > 1 ? 's' : ''} añadido${foodsCount > 1 ? 's' : ''} al carrito`
            : `🛒 ${foodsCount} ingredient${foodsCount > 1 ? 's' : ''} added to cart`,
          { duration: 4500 }
        );
        setTextMealForm({ description: '', meal_type: 'repas' });
        return; // SHORT-CIRCUIT — skip the standalone confirmation flow.
      }
      setAnalyzing(false);
      toast.success(safeData.message, { duration: 8000, closeButton: true });
      setTextMealForm({ description: '', meal_type: 'repas' });

      (async () => {
        try {
          const badgesResponse = await api.post('/badges/check');
          if (badgesResponse.data.newly_earned?.length > 0) {
            const rare = ['rare', 'epic', 'legendary'];
            const bc = badgesResponse.data.newly_earned.find(b => rare.includes(b.rarity));
            if (bc) setCelebrationBadge(bc);
            else toast.success(`${badgesResponse.data.newly_earned[0].icon} ${language === 'fr' ? 'Nouveau badge:' : 'New badge:'} ${badgesResponse.data.newly_earned[0].name}!`, { duration: 5000 });
          }
        } catch { /* non-blocking */ }
        fetchDashboardData();
      })();
    } catch (error) {
      const { msg, status, isTimeout, isPaywall } = mapAnalysisError(error, language, 'text');
      // 🚦 Paywall already handled by GlobalPaywallListener — close UI silently.
      if (isPaywall) {
        setAnalyzing(false);
        setShowUpload(false);
        setAnalysisResult(null);
        return;
      }
      console.error('[TEXT] ❌', { status, isTimeout, error });
      ErrorLogger.log(error, 'Meal_Analysis_Text', { severity: 'CRITICAL', stage: 'api_call', status, is_timeout: isTimeout });
      toast.error(msg, { duration: 7000 });
      setAnalyzing(false);
      setShowUpload(false);
    } finally {
      clearTimeout(watchdog);
    }
  };

  // ==================== NUTRITION EDITING ====================
  const saveNutritionEdits = async () => {
    if (!analysisResult?.meal_id) return;
    setSavingNutrition(true);
    try {
      const response = await api.patch(`/meals/${analysisResult.meal_id}`, editedNutrition);
      setAnalysisResult(prev => ({ ...prev, analysis: { ...prev.analysis, ...editedNutrition, manually_edited: true } }));
      toast.success(response.data.message); setIsEditingNutrition(false); fetchDashboardData();
    } catch { toast.error(t('toast.save_changes_error')); }
    finally { setSavingNutrition(false); }
  };

  const initializeEditableFoods = (foods) => {
    if (!foods) return [];
    return foods.map(food => ({ ...food, base_portion_g: food.portion_g || 100, base_protein_g: food.protein_g || 0, base_calories: food.calories || 0, base_carbs_g: food.carbs_g || 0, base_fats_g: food.fats_g || 0, base_fiber_g: food.fiber_g || 0, portion_g: food.portion_g || 100 }));
  };

  const handleFoodsChange = (updatedFoods) => {
    setEditableFoods(updatedFoods);
    const totals = updatedFoods.reduce((acc, f) => ({ total_protein_g: acc.total_protein_g + (f.protein_g || 0), total_calories: acc.total_calories + (f.calories || 0), total_carbs_g: acc.total_carbs_g + (f.carbs_g || 0), total_fats_g: acc.total_fats_g + (f.fats_g || 0), total_fiber_g: acc.total_fiber_g + (f.fiber_g || 0) }), { total_protein_g: 0, total_calories: 0, total_carbs_g: 0, total_fats_g: 0, total_fiber_g: 0 });
    setAnalysisResult(prev => ({ ...prev, analysis: { ...prev.analysis, foods_detected: updatedFoods, total_protein_g: Math.round(totals.total_protein_g * 10) / 10, total_calories: Math.round(totals.total_calories), total_carbs_g: Math.round(totals.total_carbs_g * 10) / 10, total_fats_g: Math.round(totals.total_fats_g * 10) / 10, total_fiber_g: Math.round(totals.total_fiber_g * 10) / 10 } }));
  };

  const saveMealWithEditedQuantities = async () => {
    if (!analysisResult?.meal_id || editableFoods.length === 0) return;
    setSavingNutrition(true);
    try {
      const totals = editableFoods.reduce((acc, f) => ({ total_protein_g: acc.total_protein_g + (f.protein_g || 0), total_calories: acc.total_calories + (f.calories || 0), total_carbs_g: acc.total_carbs_g + (f.carbs_g || 0), total_fats_g: acc.total_fats_g + (f.fats_g || 0), total_fiber_g: acc.total_fiber_g + (f.fiber_g || 0) }), { total_protein_g: 0, total_calories: 0, total_carbs_g: 0, total_fats_g: 0, total_fiber_g: 0 });
      const response = await api.patch(`/meals/${analysisResult.meal_id}`, { ...totals, foods_detected: editableFoods.map(f => ({ name: f.name, portion_g: f.portion_g, protein_g: f.protein_g, calories: f.calories, carbs_g: f.carbs_g, fats_g: f.fats_g, fiber_g: f.fiber_g, is_processed: f.is_processed, is_super_aliment: f.is_super_aliment })) });
      toast.success(response.data.message || t('toast.meal_updated'));
      fetchDashboardData();
    } catch { toast.error(t('toast.save_changes_error')); }
    finally { setSavingNutrition(false); }
  };

  // ==================== MODAL HANDLERS ====================
  const handleMealTypeChange = (type) => { setPhotoMealType(type); setBarcodeMealType(type); };
  const handleBarcodeClick = () => { setShowAddMealOptions(false); setShowBarcodeScanner(true); };
  // Iter. 337+ Mai 2026 — Marc/Erik/Patrick iPhone+Android camera fix:
  // (1) Trigger the file input SYNCHRONOUSLY from the user-gesture handler
  //     (no `await` before .click() — iOS Safari and Samsung Internet drop
  //     deferred clicks, which is why "ça me ramène au tableau de bord
  //     sans rien faire").
  // (2) Fire the camera-readiness check in the BACKGROUND so any warning
  //     toast still appears, without breaking the gesture chain.
  const handlePhotoClick = () => {
    setShowAddMealOptions(false);
    // Iter. 404 — Pre-warm Gemini Vision while the user is composing the
    // shot. Saves 15-25 s perceived latency on cold-start.
    prewarmVision();
    // Open camera FIRST — preserves the user-gesture token
    openNativeCamera(fileInputRef, { language });
    // Permission/private-mode hints come AFTER, fire-and-forget
    checkCameraReadiness({ language }).catch(() => {});
  };
  const handleGalleryClick = () => { setShowAddMealOptions(false); prewarmVision(); setTimeout(() => galleryInputRef.current?.click(), 300); };
  const handleDescribeClick = () => { setShowAddMealOptions(false); prewarmVision(); setTextMealForm(prev => ({ ...prev, meal_type: photoMealType })); setShowTextInput(true); };
  const handleLabelClick = () => {
    setShowAddMealOptions(false);
    prewarmVision();
    openNativeCamera(labelInputRef, { language });
    checkCameraReadiness({ language }).catch(() => {});
  };
  const handleFavoritesClick = () => { setShowAddMealOptions(false); openFavorites(); };
  const handleStartNutritionEdit = () => { setEditedNutrition({ total_protein_g: analysisResult?.analysis?.total_protein_g || 0, total_calories: analysisResult?.analysis?.total_calories || 0, total_carbs_g: analysisResult?.analysis?.total_carbs_g || 0, total_fats_g: analysisResult?.analysis?.total_fats_g || 0 }); setIsEditingNutrition(true); };
  const handleCloseAnalysis = () => { setShowUpload(false); setAnalysisResult(null); setEditableFoods([]); fetchDashboardData(); };
  const handleCancelBarcode = () => { setBarcodeResult(null); setBarcodeServings(1); };
  const handleRescanLabel = () => { setLabelResult(null); labelInputRef.current?.click(); };

  // ==================== DRAFT MEAL (MULTI-INGREDIENT) ====================
  const saveDraftMeal = async () => {
    if (draftMealItems.length === 0) return;
    // Iter. 405.d — Race-condition lock. Refuse to save while an analysis
    // is still streaming OR a previous save is in-flight. Prevents the
    // user from double-tapping "Enregistrer" and producing two meals
    // when the text/photo analysis hasn't merged into the cart yet.
    if (savingDraft || analyzing) {
      toast.info(
        language === 'fr'
          ? 'Analyse en cours… patiente une seconde.'
          : 'Analysis still running… give it a sec.',
        { duration: 2500 }
      );
      return;
    }
    // ANTI-TRICHE — Check fasting guard
    const totalCal = draftMealItems.reduce((s, i) => s + (i.calories || 0), 0);
    const fastingOk = await checkFastingGuard(totalCal);
    if (!fastingOk) return;
    setSavingDraft(true);
    try {
      const totals = draftMealItems.reduce((acc, f) => ({
        protein_g: acc.protein_g + (f.protein_g || 0),
        calories: acc.calories + (f.calories || 0),
        carbs_g: acc.carbs_g + (f.carbs_g || 0),
        fats_g: acc.fats_g + (f.fats_g || 0),
        fiber_g: acc.fiber_g + (f.fiber_g || 0),
      }), { protein_g: 0, calories: 0, carbs_g: 0, fats_g: 0, fiber_g: 0 });

      const foods = draftMealItems.map(f => ({
        name: f.name, portion_g: f.portion_g, protein_g: f.protein_g,
        calories: f.calories, carbs_g: f.carbs_g, fats_g: f.fats_g, fiber_g: f.fiber_g,
      }));

      await api.post('/meals/from-label', {
        product_name: draftMealItems.map(f => f.name).join(' + '),
        servings: 1,
        per_serving: totals,
        total_nutrition: totals,
        target_date: selectedDate || undefined,
      });

      // Update with foods_detected
      toast.success(t('toast.meal_full_saved'));
      clearDraftMeal();
      setShowDraftMeal(false);
      setSavingDraft(false);
      fetchDashboardData();
      // 🔇 Fire-and-forget badge check — never blocks the save confirmation
      (async () => {
        try {
          const br = await api.post('/badges/check');
          if (br.data.newly_earned?.length > 0) {
            const bc = br.data.newly_earned.find(b => ['rare','epic','legendary'].includes(b.rarity));
            if (bc) setCelebrationBadge(bc);
          }
        } catch { /* non-blocking */ }
      })();
    } catch {
      toast.error(t('toast.save_error'));
      setSavingDraft(false);
    }
  };

  const removeDraftItem = (id) => {
    removeDraftMealItem(id);
  };

  const scanAnotherIngredient = () => {
    setShowDraftMeal(false);
    setShowAddMealOptions(true);
  };

  // ==================== LABEL SCANNER ====================
  const handleLabelSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error(t('toast.image_select')); return; }
    try { const base64 = await compressImage(file, { maxWidth: 1000, quality: 0.8 }); await scanNutritionLabel(base64); }
    catch (err) { toast.error(t('toast.image_process_error')); }
  };

  const scanNutritionLabel = async (imageBase64) => {
    setScanningLabel(true); setShowLabelScanner(true); setLabelResult(null); setLabelProductName('');
    try { const response = await api.post('/meals/scan-label', { image_base64: imageBase64, servings: labelServings }); setLabelResult(response.data); setLabelProductName(response.data.product_name || ''); toast.success(response.data.message); }
    catch { toast.error(t('toast.label_scan_error')); setShowLabelScanner(false); }
    finally { setScanningLabel(false); }
  };

  const addLabelAsFood = async () => {
    if (!labelResult || !labelResult.per_serving) return;
    const productName = labelProductName.trim() || labelResult.product_name || t('toast.product_scanned_default');
    const perServing = labelResult.per_serving;
    const servings = labelServings;
    const newItem = {
      id: Date.now(),
      name: productName,
      portion_g: Math.round((labelResult.serving_size_g || 100) * servings),
      protein_g: Math.round((perServing.protein_g || 0) * servings * 10) / 10,
      calories: Math.round((perServing.calories || 0) * servings),
      carbs_g: Math.round((perServing.carbs_g || 0) * servings * 10) / 10,
      fats_g: Math.round((perServing.fats_g || 0) * servings * 10) / 10,
      fiber_g: Math.round((perServing.fiber_g || 0) * servings * 10) / 10,
      source: 'label',
    };
    addDraftMealItems([newItem]);
    setShowLabelScanner(false); setLabelResult(null); setLabelServings(1); setLabelProductName('');
    setShowDraftMeal(true);
    toast.success(t('toast.product_added_cart', { name: productName }));
  };

  // ==================== BARCODE SCANNER ====================
  const handleBarcodeScan = async (barcode) => {
    setLookingUpBarcode(true); setBarcodeResult(null); setShowBarcodeScanner(false);
    try {
      const response = await api.get(`/scan/barcode/${barcode}`);
      if (response.data.found) { setBarcodeResult(response.data); toast.success(language === 'fr' ? `Produit trouve: ${response.data.product_name}` : `Product found: ${response.data.product_name}`); }
      else { toast.info(language === 'fr' ? "Produit non trouve. Essayez de scanner l'etiquette." : 'Product not found. Try scanning the label.'); setTimeout(() => labelInputRef.current?.click(), 500); }
    } catch { toast.error(language === 'fr' ? 'Erreur de recherche du produit' : 'Product lookup error'); }
    finally { setLookingUpBarcode(false); }
  };

  const addBarcodeProductAsMeal = async () => {
    if (!barcodeResult) return;
    // Add to draft instead of saving immediately
    const n = barcodeResult.nutrition_per_100g || {};
    const servings = barcodeServings;
    const newItem = {
      id: Date.now(),
      name: barcodeResult.product_name + (barcodeResult.brand ? ` (${barcodeResult.brand})` : ''),
      portion_g: Math.round(100 * servings),
      protein_g: Math.round((n.protein || 0) * servings * 10) / 10,
      calories: Math.round((n.calories || 0) * servings),
      carbs_g: Math.round((n.carbs || 0) * servings * 10) / 10,
      fats_g: Math.round((n.fat || 0) * servings * 10) / 10,
      fiber_g: Math.round((n.fiber || 0) * servings * 10) / 10,
      source: 'barcode',
    };
    addDraftMealItems([newItem]);
    setBarcodeResult(null); setBarcodeServings(1);
    setShowDraftMeal(true);
    toast.success(language === 'fr' ? `${barcodeResult.product_name} ajoute au panier` : `${barcodeResult.product_name} added to cart`);
    try { playSound('meal_logged'); } catch (_) {}
  };

  // ==================== FAVORITES ====================
  const fetchFavorites = async () => { setLoadingFavorites(true); try { const r = await api.get('/scan/favorites'); setFavorites(r.data.favorites || []); } catch { toast.error(language === 'fr' ? 'Erreur de chargement des favoris' : 'Error loading favorites'); } finally { setLoadingFavorites(false); } };
  const openFavorites = async () => { await fetchFavorites(); setShowFavorites(true); };
  const addFavoriteAsMeal = async (favorite) => {
    setAddingFavorite(true);
    try {
      // Catchup mode: forward the selected past date so the meal is logged
      // on that day instead of "today". Without this body, the backend
      // defaults to now() and the favorite would land on the current date.
      const body = selectedDate ? { target_date: selectedDate } : {};
      await api.post(`/scan/favorites/${favorite.barcode}/log-meal`, body);
      toast.success(language === 'fr' ? `${favorite.product_name} ajoute!` : `${favorite.product_name} added!`);
      try { playSound('meal_logged'); } catch (_) {}
      setShowFavorites(false);
      setAddingFavorite(false);
      fetchDashboardData();
      // 🔇 Fire-and-forget badge check
      (async () => {
        try {
          const br = await api.post('/badges/check');
          if (br.data.newly_earned?.length > 0) {
            const bc = br.data.newly_earned.find(b => ['rare','epic','legendary'].includes(b.rarity));
            if (bc) setCelebrationBadge(bc);
          }
        } catch { /* non-blocking */ }
      })();
    } catch {
      toast.error(language === 'fr' ? "Erreur d'ajout" : 'Error adding meal');
      setAddingFavorite(false);
    }
  };
  const deleteFavorite = async (barcode) => { try { await api.delete(`/scan/favorites/${barcode}`); toast.success(language === 'fr' ? 'Favori supprime' : 'Favorite removed'); setFavorites(prev => prev.filter(f => f.barcode !== barcode)); } catch { toast.error(language === 'fr' ? 'Erreur de suppression' : 'Error deleting'); } };

  // Iter. 396 · Lot #12 — Inline rename of a favorite. The backend
  // PATCH /scan/favorites/{barcode}/rename stores a `custom_name`; passing
  // an empty string unsets the override (auto-name is restored).
  const renameFavorite = async (barcode, customName) => {
    try {
      const res = await api.patch(`/scan/favorites/${barcode}/rename`, { custom_name: customName || null });
      const newName = res?.data?.custom_name || null;
      setFavorites(prev => prev.map(f => (
        f.barcode === barcode ? { ...f, custom_name: newName } : f
      )));
      toast.success(
        newName
          ? (language === 'fr' ? '✏️ Favori renommé' : language === 'es' ? '✏️ Favorito renombrado' : '✏️ Favorite renamed')
          : (language === 'fr' ? '↩️ Nom restauré' : language === 'es' ? '↩️ Nombre restaurado' : '↩️ Name restored'),
        { duration: 2200 }
      );
    } catch (err) {
      toast.error(
        language === 'fr' ? 'Erreur lors du renommage' :
        language === 'es' ? 'Error al renombrar' : 'Rename failed'
      );
      throw err;
    }
  };

  const deleteMeal = async (mealId) => {
    setDeletingMeal(true);
    try { await api.delete(`/meals/${mealId}`); toast.success(language === 'fr' ? 'Repas supprime' : 'Meal deleted'); setMealToDelete(null); fetchDashboardData(); }
    catch { toast.error(language === 'fr' ? 'Erreur de suppression' : 'Error deleting'); }
    finally { setDeletingMeal(false); }
  };

  const saveCurrentMealAsFavorite = async () => {
    if (!analysisResult?.analysis) return;
    const analysis = analysisResult.analysis;
    const foodName = analysis.foods_detected?.[0]?.name || 'Mon repas';
    try {
      await api.post('/scan/favorites', { barcode: `meal_${Date.now()}`, product_name: foodName, brand: '', image_url: '', nutrition_per_100g: { calories: analysis.total_calories ?? 0, protein: analysis.total_protein_g ?? 0, carbs: analysis.total_carbs_g ?? 0, fat: analysis.total_fats_g ?? 0, fiber: analysis.total_fiber_g ?? 0 } });
      toast.success(language === 'fr' ? 'Ajoute aux favoris!' : 'Added to favorites!');
    } catch { toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Error saving'); }
  };

  // ==================== COMPUTED VALUES ====================
  // Defensive coercion — `stats.today_*` from the backend can occasionally be
  // a string for legacy meals (string-typed macros). The division below would
  // become string concatenation otherwise, producing NaN and crashing the
  // dashboard render.
  const _safeNum = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const proteinProgress = stats?.protein_target_min ? Math.min(100, (_safeNum(stats.today_protein_g) / _safeNum(stats.protein_target_min)) * 100) : 0;
  const effectiveCalorieTarget = _safeNum(stats?.calorie_target) || 2000;
  const calorieProgress = effectiveCalorieTarget ? Math.min(100, (_safeNum(stats?.today_calories) / effectiveCalorieTarget) * 100) : 0;
  const fiberTarget = _safeNum(stats?.fiber_target) || 35;
  const fiberProgress = stats ? Math.min(100, (_safeNum(stats.today_fiber_g) / fiberTarget) * 100) : 0;

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-[80vh]"><Loader2 className="w-12 h-12 text-primary animate-spin" /></div></Layout>;
  }

  // Terrain Profile Onboarding — show as dismissable overlay, NOT a blocker
  if (showTerrainOnboarding) {
    return (
      <TerrainOnboardingView
        language={language}
        onClose={() => setShowTerrainOnboarding(false)}
      />
    );
  }

  // Pull-to-refresh: revalidate everything in parallel
  const handlePullRefresh = async () => {
    if (!user) return;
    const uid = user.user_id;
    // Wipe SWR for this user so swrLive forces a network round-trip
    invalidatePrefix(`dash:`);
    try {
      await Promise.all([
        fetchDashboardData(),
        api.get(`/client-photos/${uid}`).then(r => { if (r.data.photo_url) { setClientPhoto(r.data.photo_url); writeCache(`dash:clientphoto:${uid}`, r.data.photo_url); } }).catch(() => {}),
        api.get('/dashboard/vitality').then(r => { setVitality(r.data); writeCache(`dash:vitality:${uid}`, r.data); }).catch(() => {}),
        api.get('/users/clinical-mode').then(r => { setClinicalMode(r.data.clinical_mode); writeCache(`dash:clinical:${uid}`, r.data.clinical_mode); }).catch(() => {}),
        api.get('/leaderboard/my-stats').then(r => { setTripleStats(r.data); writeCache(`dash:triple:${uid}`, r.data); }).catch(() => {}),
        api.get('/dashboard/incomplete-days').then(r => { setIncompleteDays(r.data?.incomplete_days || []); writeCache(`dash:incomplete:${uid}`, r.data?.incomplete_days || []); }).catch(() => {}),
      ]);
    } catch { /* swallow — UI keeps last data */ }
  };

  return (
    <Layout>
      <PullToRefresh onRefresh={handlePullRefresh} language={language}>
      <WelcomeConfetti show={showWelcomeConfetti} onDone={() => setShowWelcomeConfetti(false)} />

      {/* Iter. 411.g — Rank-up motivation (Clash leaderboard climb) */}
      <RankUpModal userId={user?.user_id} />
      {/* Iter. 411.cc — Profile grade progression (Recrue → Legende, etc.) */}
      <ProfileGradeUpModal userId={user?.user_id} />

      {/* Iter. 406.fr.11 — Coach YUMIX epic arrival cinematic (one-shot) */}
      <CoachYumixIntroModal
        open={showCoachIntro}
        onClose={() => setShowCoachIntro(false)}
        language={language}
      />

      {/* Iter. 406.k — Weekly pillar progression popup (dopaminergic) */}
      <PillarProgressionPopup userId={user?.user_id} language={language} />

      {/* Iter. 406.n — Orion Symbiose J7 celebration modal (recurring weekly quest) */}
      <OrionSymbioseModal
        open={!!orionCelebration}
        onClose={async () => {
          if (orionCelebration?.quest_id) {
            try {
              await api.post('/weekly-quest/celebration-shown', {
                quest_id: orionCelebration.quest_id,
              });
            } catch { /* ignore */ }
          }
          setOrionCelebration(null);
        }}
        celebration={orionCelebration?.j7_celebration}
        questDays={orionCelebration?.days}
        cycleNumber={orionCelebration?.cycle_number}
        language={language}
      />

      {/* Iter. 406.p — Welcome challenge J7 → "Concevoir mon plan 6 semaines" CTA */}
      <OrionSymbioseModal
        open={welcomeOrionOpen}
        onClose={() => setWelcomeOrionOpen(false)}
        mode="welcome_completion"
        onCreatePlan={() => {
          setWelcomeOrionOpen(false);
          setShowWorkoutOnboarding(true);
        }}
        celebration={{
          base_bonus: 200,
          constancy_bonus: 350,
          validated_days: 7,
          total_bonus: 550,
        }}
        questDays={Array.from({ length: 7 }, (_, i) => ({ day_index: i + 1 }))}
        language={language}
      />

      {/* Health Profile Onboarding Modal — Veterans catch-up */}
      <HealthProfileOnboardingModal
        open={showHealthOnboarding}
        onComplete={() => setShowHealthOnboarding(false)}
      />
      
      <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="client-dashboard">
        {/* Days-Active badge + Path pill row (Iter. 411.mm18) */}
        <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
          <DaysActiveBadge />
          {user?.day8_path && (() => {
            const isAthlete = user.day8_path === 'athlete';
            const rgb = isAthlete ? '251,146,60' : '167,139,250';   // orange | violet-400
            const textCol = isAthlete ? '#fed7aa' : '#ddd6fe';
            return (
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest"
                data-testid="dashboard-path-pill"
                title={language === 'fr'
                  ? `Parcours actif depuis le ${user.day8_picked_at?.slice(0,10) || 'J8'}`
                  : `Active path since ${user.day8_picked_at?.slice(0,10) || 'D8'}`}
                style={{
                  background: `linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(${rgb},0.22) 100%)`,
                  border: `1px solid rgba(${rgb},0.55)`,
                  boxShadow: `0 0 14px rgba(${rgb},0.45), inset 0 1px 0 rgba(255,255,255,0.06)`,
                  backdropFilter: 'blur(6px)',
                  color: textCol,
                  textShadow: `0 0 6px rgba(${rgb},0.65)`,
                }}
              >
                <span style={{ filter: `drop-shadow(0 0 5px rgba(${rgb},0.9))` }}>
                  {isAthlete ? '🏋️' : '❤️'}
                </span>
                {isAthlete
                  ? (language === 'fr' ? 'Parcours Athlète' : 'Athlete path')
                  : (language === 'fr' ? 'Parcours Lifestyle' : 'Lifestyle path')}
              </div>
            );
          })()}
        </div>

        {/* Header */}
        <DashboardHeader
          user={user} language={language} t={t}
          selectedDate={selectedDate} setSelectedDate={setSelectedDate}
          showDatePicker={showDatePicker} setShowDatePicker={setShowDatePicker}
          incompleteDays={incompleteDays} clinicalMode={clinicalMode}
        />

        {/* 🌍 Locality-aware welcome — once per user (cuisine examples
            + greeting matched to navigator.language region). Wrapped in a
            silent ErrorBoundary so a buggy delight feature can NEVER take
            the whole Dashboard down (Lot #11 hotfix). */}
        <ErrorBoundary silent label="locality-welcome">
          <LocalityWelcomeBanner userId={user?.id} />
        </ErrorBoundary>

        {/* 🚨 Iter. 406.fr.73 — Discreet pillar-alert bubble. */}
        <ErrorBoundary silent label="pillar-alert-bubble">
          <PillarAlertBubble />
        </ErrorBoundary>

        {/* 🛰️ HUD Mission Tracker — "mission-control" live band
            (Yuri spec 2026-02 Clean & Glow): time · Mission #N · priority pillars. */}
        <HudMissionTracker stats={stats} language={language} />

        {/* 🌸 Female Health quick-access (women only — Yuri spec 2026-02 Iter. 312).
            Mauve/Fuchsia glow signature, opens /female-health hub on click. */}
        {user?.biological_sex === 'femme' && (
          <button
            type="button"
            data-testid="dashboard-female-health-icon"
            onClick={() => navigate('/female-health')}
            className="w-full mb-4 group rounded-xl px-4 py-3 flex items-center justify-between transition-transform hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(90deg, rgba(192,132,252,0.15), rgba(232,121,249,0.10), rgba(244,114,182,0.12))',
              border: '1px solid rgba(192,132,252,0.50)',
              boxShadow: '0 0 18px rgba(192,132,252,0.40), inset 0 0 0 1px rgba(232,121,249,0.10)',
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-fuchsia-500/30 to-purple-500/30 flex items-center justify-center"
                style={{ boxShadow: '0 0 10px rgba(192,132,252,0.5)' }}>
                <Sparkles className="w-4 h-4 text-fuchsia-200" style={{ filter: 'drop-shadow(0 0 6px rgba(232,121,249,0.85))' }} />
              </div>
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-fuchsia-300"
                  style={{ textShadow: '0 0 6px rgba(232,121,249,0.6)' }}>
                  {language === 'fr' ? 'Santé Féminine' : 'Female Health'}
                </p>
                <p className="text-xs text-foreground/80">
                  {language === 'fr' ? 'Cycle · Énergie · Symptômes' : 'Cycle · Energy · Symptoms'}
                </p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-fuchsia-300 group-hover:translate-x-0.5 transition-transform" />
          </button>
        )}

        {/* 🛡️ Score de Discipline — KPI #1 promu en TOUT premier widget après
            le HUD (Yuri spec 2026-02 Iter. 318). Agrège les 4 piliers du jour
            (Protéines, Calories, Jeûne, Activité) avec gold-electric glow. */}
        {!isEpure && !isJunior && user?.role !== 'coach' && (
          <div className="mb-4" data-testid="dashboard-discipline-section">
            <DisciplineScore />
          </div>
        )}

        {/* 🧠 Daily Mindset reflection banner — surfaces for ALL client users
            every morning until they answer. Doubles as the onboarding
            touchpoint for week-1 clients (Yuri spec 2026-05-06). */}
        {user?.role !== 'coach' && user?.role !== 'super_admin' && user?.role !== 'admin' && (
          <>
            <MindsetMonthlyRecapBanner />
            <MindsetWeeklyRecapBanner />
            <DailyMindsetBanner />
          </>
        )}

        {/* Iter. 406.r — Welcome Challenge takes ABSOLUTE PRIORITY for new
            users. Pinned at the top right after the Briefing (Mindset banner)
            and BEFORE the plan-of-attack, steps, water, etc. Auto-disappears
            once the welcome challenge is completed + dismissed (returns null
            inside the component itself). */}
        {user?.role !== 'coach' && user?.role !== 'super_admin' && user?.role !== 'admin' && (
          <div
            className={`relative mb-4 ${welcomeActive ? 'onboarding-priority-glow' : ''}`}
            data-testid="welcome-priority-wrapper"
            data-welcome-active={welcomeActive ? 'true' : 'false'}
          >
            <WelcomeChallengeCard
              priorityMode={welcomeActive}
              onMissionClick={(day) => {
                if (day === 5) setShowWorkoutOnboarding(true);
              }}
            />
          </div>
        )}

        {/* 🎯 Path-priority block — placed AFTER the pilot profile header
            (Yuri spec 2026-05-06): the 3 shortcuts feel more grounded once
            the user has been "introduced" by their pilot identity above. */}
        <Day8PriorityBlock user={user} language={language} />

        <RamadanBanner />
        <TacticalShiftBanner />

        {/* Iter. 411.h — Petit raccourci Clash des Meutes (demande Youri).
            Auto-hidden pour staff + users sans team. */}
        <ClashShortcut />

        {/* Iter. 406.p — Permanent CTA banner: "Activer mon cycle 6 semaines"
            Auto-hides as soon as a 6-week cycle is active. */}
        {user?.role !== 'coach' && user?.role !== 'super_admin' && user?.role !== 'admin' && (
          <div className="mb-4">
            <ActivateCycleBanner
              language={language}
              onOpenWizard={() => setShowWorkoutOnboarding(true)}
            />
          </div>
        )}

        <TacticalWeeklyChallenge />

        {/* Iter. 406.n — 7-Day Quest + Orion celebration */}
        {user?.role !== 'coach' && user?.role !== 'super_admin' && user?.role !== 'admin' && (
          <div className="mb-4">
            <WeeklyQuestCard
              language={language}
              onJ7Completed={() => fetchOrionCelebration()}
            />
          </div>
        )}

        <ChallengeNotificationBanner />
        <RecurringMealCard selectedDate={selectedDate} />

        {/* Iter. 411.c — Missions J7 et photos INVERSÉES (demande utilisateur):
            La prise de photos "Avant YUMIX" est désormais une mission
            intermédiaire (jour 3-6 selon le profil) pour capturer une baseline
            AVANT que le corps ne change, et le J7 devient la mission
            "Log un entraînement" — climax du défi (cf. welcome_challenge.py).
            Yuri: friction nulle à l'embarquement, photo prise tôt, culmination
            physique en fin de semaine. */}

        {/* QUEBEC users — Calendly booking with Youri */}
        {access?.user_type === 'QUEBEC' && access?.calendly_url && (
          <div className="mb-4 flex justify-center" data-testid="calendly-section">
            <CalendlyButton access={access} />
          </div>
        )}

        {/* PAYWALL — bannière essai expirant J-3 → J0 */}
        {access && !access.is_premium && access.is_trial_active && access.trial_days_remaining <= 3 && (
          <div className="mb-4 p-3 rounded-xl bg-orange-500/10 border border-orange-500/30 text-center" data-testid="paywall-trial-banner">
            <p className="text-sm text-orange-300 font-bold">
              ⏰ Il te reste <strong>{access.trial_days_remaining} jour{access.trial_days_remaining > 1 ? 's' : ''}</strong> d'essai gratuit
            </p>
            <p className="text-xs text-orange-300/70 mt-1">
              Active ton accès Premium pour continuer le Scanner IA, les plans personnalisés et le Coach YUMIX.
            </p>
          </div>
        )}

        {/* Épuré Mode Banner */}
        {isEpure && (
          <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-cyan-600/10 to-blue-600/10 border border-cyan-500/20" data-testid="epure-mode-banner">
            <p className={`font-heading uppercase tracking-tight text-cyan-400 ${isEpure ? 'text-base' : 'text-sm'}`}>
              {language === 'fr' ? 'Mode Simplifie Actif' : 'Simplified Mode Active'}
            </p>
            <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Interface epuree — utilise le bouton + pour loguer' : 'Clean interface — use the + button to log'}</p>
          </div>
        )}

        {/* Junior Mode Banner */}
        {isJunior && (
          <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-green-600/10 to-emerald-600/10 border border-green-500/20" data-testid="junior-mode-banner">
            <p className="font-heading text-base uppercase tracking-tight text-green-400">
              {language === 'fr' ? 'Mode Junior Actif' : 'Junior Mode Active'}
            </p>
            <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Focus sur l\'énergie et les bonnes habitudes!' : 'Focus on energy and good habits!'}</p>
          </div>
        )}

        {/* MealReminder removed (Yuri spec 2026-05-06) — the (+) button
            already covers the meal-snap CTA. Less coach-image redundancy. */}

        {/* Admin Coaching shortcut moved into Analyse Avancée section
            (Yuri spec 2026-02) so the top-of-fold stays focused on the
            client experience. */}
        <div className="mb-6"><PushNotificationToggle /></div>

        {/* ============ DASHBOARD LAYOUT (Yuri cleanup 2026-05-06) ============
            Priority visible top-of-fold:
              1. 4 Pillars (Mon Plan / Nutrition / Social & Défis / Mindset)
              2. Performance widgets (Triple Victoire + Mindset streak)
              3. Hydration (blue-glow) + Fasting (ONLY if active)
            Everything else is collapsed into "Analyse Avancée" below.
            =============================================================== */}

        {/* SECTION 1: Mission du Jour (macros compact) */}
        <div className="mb-4" data-testid="unified-summary">
          <DashboardMission
            language={language} t={t} stats={stats}
            proteinProgress={proteinProgress}
            effectiveCalorieTarget={effectiveCalorieTarget}
            calorieProgress={calorieProgress}
            isJunior={isJunior}
            isEpure={isEpure}
            adaptLabel={adaptLabel}
          />
        </div>

        {/* SECTION 1bis: Score de Discipline déplacé tout en haut (Iter. 318) —
            ce bloc reste ici en commentaire pour traçabilité git. Le widget est
            maintenant rendu juste après le bouton Santé Féminine. */}

        {/* SECTION 2: Triple Victoire — Pas + Protéines + Entraînement */}
        <div className="mb-4">
          <TripleVictoryWidget
            stats={stats}
            language={language}
            onNavigate={(path) => navigate(path)}
          />
        </div>

        {/* SECTION 2bis: Debrief Journalier YUMIX (Mai 2026 — Yuri spec)
            Promoted from the "Analyse avancée" tab to the dashboard hero
            slot. Wrapped in a premium "Aurora Glow" frame: animated dual-
            color gradient halo + subtle border shimmer so it draws the eye
            without overpowering the cards above/below. */}
        <div className="mb-4 relative group" data-testid="dashboard-debrief-section">
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-[2px] rounded-2xl opacity-90 blur-[6px] yumix-debrief-aurora"
            style={{
              background: 'linear-gradient(110deg, #f97316 0%, #ec4899 28%, #8b5cf6 55%, #22d3ee 82%, #f97316 100%)',
              backgroundSize: '300% 300%',
            }}
          />
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{
              boxShadow:
                '0 0 0 1px rgba(251,146,60,0.45), 0 0 30px rgba(236,72,153,0.30), 0 0 60px rgba(139,92,246,0.20)',
            }}
          >
            <ErrorBoundary silent label="morning-debrief">
              <MorningDebrief />
            </ErrorBoundary>
          </div>
        </div>

        {/* Battle Plan Tracker — compact progress tracker for the 7-day plan */}
        <div className="mb-4" data-testid="dashboard-battle-plan-tracker">
          <ErrorBoundary silent label="battle-plan-tracker">
            <BattlePlanTracker />
          </ErrorBoundary>
        </div>

        {/* 🛡️ Iter. 406.y — Medical Shield card: only renders when the user has
            a non-empty injuries_description. Reassures users that the AI is
            adapting their plan around their condition, and offers a 1-click
            "Mettre à jour" path that doesn't require re-running plan generation. */}
        <div className="mb-4" data-testid="dashboard-medical-shield">
          <ErrorBoundary silent label="medical-shield-card">
            <MedicalShieldCard />
          </ErrorBoundary>
        </div>

        {/* SECTION 3: Steps histogram — the orange-fluo hero chart */}
        <div className="mb-4" data-testid="dashboard-steps-section">
          <ErrorBoundary silent label="steps-widget">
            <StepsWidget />
          </ErrorBoundary>
        </div>

        {/* SECTION 4: Hydration (Blue Glow) — Fasting only if active */}
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div data-testid="dashboard-hydration-section">
            <ErrorBoundary silent label="hydration-gauge">
              <HydrationGauge />
            </ErrorBoundary>
          </div>
          <div data-testid="dashboard-fasting-section">
            <ErrorBoundary silent label="fasting-widget">
              <FastingWidget hideWhenInactive />
            </ErrorBoundary>
          </div>
        </div>

        {/* SECTION 4.5: Coach YUMIX — Analyse des Habitudes (emerald glow, collapsible) */}
        {!isEpure && !isJunior && (
          <div className="mb-4" data-testid="dashboard-pattern-insights-section">
            <ErrorBoundary silent label="pattern-insights">
              <PatternInsights />
            </ErrorBoundary>
          </div>
        )}

        {/* ============ ANALYSE AVANCÉE — collapsed by default ============
            All secondary widgets (supplements, discipline, challenges,
            coaches, body metrics, etc.) live behind this toggle so the
            Dashboard stays short & punchy.
            Yuri spec 2026-02: large + electric-purple-glow toggle to make
            it OBVIOUS that more content is unlocked below. */}
        {!isEpure && !isJunior && (
          <details className="mb-4 rounded-2xl border border-purple-400/40 bg-gradient-to-br from-purple-950/40 via-card/60 to-violet-950/30 group" data-testid="dashboard-advanced-section"
            style={{ boxShadow: '0 0 18px rgba(168,85,247,0.32), inset 0 0 0 1px rgba(168,85,247,0.12)' }}>
            <summary className="flex items-center justify-between gap-3 cursor-pointer select-none px-5 py-4 list-none rounded-2xl transition-all hover:scale-[1.005] active:scale-[0.99]">
              <div className="flex items-center gap-3 min-w-0">
                <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 ring-1 ring-purple-300/50 flex items-center justify-center"
                     style={{ boxShadow: '0 0 14px rgba(168,85,247,0.6)' }}>
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-purple-300/90 leading-none mb-1">
                    {language === 'fr' ? 'Débloqué' : 'Unlocked'}
                  </p>
                  <h3 className="font-heading text-base sm:text-lg uppercase tracking-tight text-foreground leading-none">
                    {language === 'fr' ? 'Analyses Avancées' : 'Advanced Analytics'}
                  </h3>
                </div>
              </div>
              <span
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-500/30 to-violet-500/30 ring-1 ring-purple-300/60 text-purple-100 group-open:hidden"
                style={{ boxShadow: '0 0 10px rgba(168,85,247,0.55)' }}
              >
                <span className="text-[10px] font-black uppercase tracking-widest">
                  {language === 'fr' ? 'Ouvrir' : 'Open'}
                </span>
                <ChevronDown className="w-3.5 h-3.5" />
              </span>
              <span className="shrink-0 text-[10px] uppercase tracking-widest text-muted-foreground hidden group-open:inline">
                {language === 'fr' ? 'Réduire' : 'Hide'} ▴
              </span>
            </summary>
            <div className="px-2 sm:px-3 pb-3 pt-1 space-y-3">
              {/* Admin Coaching Quick Access (super_admin / coach only) */}
              {(user?.role === 'super_admin' || user?.role === 'coach') && (
                <div
                  className="p-3 rounded-xl bg-gradient-to-r from-amber-600/20 to-orange-600/20 border border-amber-500/30 flex items-center justify-between cursor-pointer hover:from-amber-600/30 hover:to-orange-600/30 transition-all"
                  onClick={() => navigate('/admin')}
                  data-testid="admin-coaching-shortcut"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <span className="text-amber-400 text-lg font-bold">C</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-amber-300">{t('dashboard.admin_coaching')}</p>
                      <p className="text-[11px] text-amber-400/60">{t('dashboard.admin_coaching_subtitle')}</p>
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-amber-400/60 -rotate-90" />
                </div>
              )}
              {/* Discipline Score promoted to top-of-fold (Yuri spec 2026-02
                  Clean & Glow) — it's now the #1 KPI of the dashboard. */}

              {/* Supplements */}
              <div data-testid="dashboard-supplements-section"><SupplementsWidget /></div>

              {/* ⚔️ Clash teaser */}
              <ClashWidget />

              {/* Badge progression + streak share */}
              <NextBadgeWidget />
              <StreakShareButton variant="inline" />

              {/* Coaching & check-ins */}
              <PainCheckPopup />
              <FridayDebrief />
              {/* MorningDebrief moved to the main Dashboard (above StepsWidget)
                  as a hero card with Aurora Glow — Mai 2026 Yuri spec. */}

              {/* HeroFemaleWatcher removed (Yuri spec 2026-02 Clean & Glow):
                  redundant coach image — the daily question + coach feedback
                  modal already cover the empathic touchpoint. */}

              {/* Injury / retention / pack */}
              <InjuryAdaptationCard onPlanChanged={() => fetchDashboardData()} />
              <RetentionChallenges />
              <PackChallenge compact />
              <DebriefExtraMiles />

              {/* Vitality (if enough data) */}
              {vitality && vitality.total_meals > 0 && (
                <VitalityCard data={vitality} language={language} />
              )}

              {/* Family / budget */}
              <BudgetHeroWidget />
              {/* FamilyProfileWidget moved to Settings (Yuri spec 2026-02
                  Phase 2): static profile info doesn't belong on a daily
                  performance dashboard. */}

              {/* Client Photo — Yuri spec 2026-02 Phase 2: silver/rose glow
                  signature for body measurements / silhouette */}
              {clientPhoto && (
                <div
                  className="rounded-xl overflow-hidden border border-pink-300/40"
                  style={{
                    background: 'linear-gradient(135deg, rgba(244,114,182,0.10), rgba(226,232,240,0.06) 60%)',
                    boxShadow: '0 0 14px rgba(244,114,182,0.40), 0 0 5px rgba(226,232,240,0.30), inset 0 0 0 1px rgba(244,114,182,0.12)',
                  }}
                >
                  <div className="flex items-center gap-3 p-3">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${clientPhoto}`}
                      alt={language === 'fr' ? 'Ma photo' : 'My photo'}
                      className="w-16 h-16 object-cover rounded-lg ring-1 ring-pink-300/40"
                      style={{ boxShadow: '0 0 8px rgba(244,114,182,0.45)' }}
                      data-testid="my-client-photo"
                    />
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.28em] font-bold text-pink-300 mb-0.5">
                        {language === 'fr' ? 'Silhouette' : 'Silhouette'}
                      </p>
                      <h3 className="font-heading text-sm uppercase tracking-tight text-foreground">
                        {language === 'fr' ? 'Ma photo' : 'My photo'}
                      </h3>
                      <p className="text-[10px] text-muted-foreground">{language === 'fr' ? 'Ajoutée par ton coach' : 'Added by your coach'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Deep stats — weight trend, sleep, body measurements, etc. */}
              <DashboardAdvancedStats
                language={language} t={t} stats={stats}
                proteinProgress={proteinProgress}
                effectiveCalorieTarget={effectiveCalorieTarget}
                calorieProgress={calorieProgress}
                fiberProgress={fiberProgress} fiberTarget={fiberTarget}
                badges={badges} weeklyData={weeklyData} tripleStats={tripleStats}
                showDetails={showDetails} setShowDetails={setShowDetails}
                setShowBadges={setShowBadges}
                setShowWorkout={setShowWorkout}
                deleteWorkout={deleteWorkout}
              />
            </div>
          </details>
        )}

        {/* Always-visible lifecycle modals (don't take layout space).
            Iter. 406.r — WelcomeChallengeCard moved to the TOP-priority slot
            (right after the Briefing) so it stops being buried under steps
            and water modules. */}
        <Day7CelebrationModal language={language} />
        <Day8PivotModal language={language} user={user} />

        {/* Rattrapage Banner — only when relevant */}
        {!selectedDate && incompleteDays.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-4 p-3 bg-orange-500/5 border border-orange-500/20 rounded-xl cursor-pointer hover:border-orange-500/40 transition-all" onClick={() => { setShowDatePicker(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }} data-testid="rattrapage-banner">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-orange-500/15 flex items-center justify-center flex-shrink-0"><CalendarDays className="w-4 h-4 text-orange-500" /></div>
              <div>
                <p className="text-xs font-bold text-orange-400">{language === 'fr' ? `${incompleteDays.length} journee${incompleteDays.length > 1 ? 's' : ''} incomplete${incompleteDays.length > 1 ? 's' : ''}` : `${incompleteDays.length} incomplete day${incompleteDays.length > 1 ? 's' : ''}`}</p>
                <p className="text-[10px] text-muted-foreground">{language === 'fr' ? 'Rattrape ton Extra Mile ici' : 'Catch up here'}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* FAB + Action Buttons */}
        <DashboardFAB
          showFab={showFab} setShowFab={setShowFab}
          todayLogCount={todayLogCount} language={language}
          onScanClick={() => { setShowAddMealOptions(true); prewarmVision(); }}
          onWorkoutClick={() => setShowWorkout(true)}
          onPhotoClick={handlePhotoClick}
          onTextClick={() => { setShowTextInput(true); prewarmVision(); }}
          onLabelClick={() => { prewarmVision(); labelInputRef.current?.click(); }}
          onFavoritesClick={openFavorites}
          fileInputRef={fileInputRef} galleryInputRef={galleryInputRef} labelInputRef={labelInputRef}
          handleFileSelect={handleFileSelect} handleLabelSelect={handleLabelSelect}
        />

        {/* Modals */}
        <AddMealOptionsModal
          open={showAddMealOptions}
          onOpenChange={setShowAddMealOptions}
          photoMealType={photoMealType}
          onMealTypeChange={handleMealTypeChange}
          onPhotoClick={handlePhotoClick}
          onGalleryClick={handleGalleryClick}
          onDescribeClick={handleDescribeClick}
          onLabelClick={handleLabelClick}
          onFavoritesClick={handleFavoritesClick}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          dateFocus={location.state?.dateFocus === true}
        />
        <TextMealInputModal open={showTextInput} onOpenChange={setShowTextInput} textMealForm={textMealForm} onFormChange={setTextMealForm} onSubmit={analyzeTextMeal} analyzing={analyzing} />
        <MealAnalysisErrorBoundary onReset={handleCloseAnalysis}>
          <MealAnalysisModal open={showUpload} onOpenChange={setShowUpload} analyzing={analyzing} optimizingImage={optimizingImage} streamPhase={streamPhase} streamLLMElapsedMs={streamLLMElapsedMs} onAbortAnalysis={handleAbortAnalysis} analysisResult={analysisResult} isEditingNutrition={isEditingNutrition} editedNutrition={editedNutrition} onEditedNutritionChange={setEditedNutrition} savingNutrition={savingNutrition} onStartEdit={handleStartNutritionEdit} onCancelEdit={() => { setIsEditingNutrition(false); setEditedNutrition({}); }} onSaveEdit={saveNutritionEdits} editableFoods={editableFoods} onFoodsChange={handleFoodsChange} onSaveFoodsEdit={saveMealWithEditedQuantities} onSaveAsFavorite={saveCurrentMealAsFavorite} onClose={handleCloseAnalysis} t={t} />
        </MealAnalysisErrorBoundary>

        {/* 🩹 Injury-to-Workout pipeline modal — wired from dashboard "Mode blessure" button */}
        <InjuryCaptureDialog
          open={injuryDialogOpen}
          onOpenChange={setInjuryDialogOpen}
          onPlanGenerated={() => fetchDashboardData()}
        />
        <LowQualityPhotoModal
          open={!!qualityCheck}
          onOpenChange={(v) => { if (!v) setQualityCheck(null); }}
          quality={qualityCheck?.quality}
          language={language}
          onAnalyzeAnyway={async () => {
            const b64 = qualityCheck?.base64;
            setQualityCheck(null);
            if (!b64) return;
            setAnalysisResult(null);
            setAnalyzing(true);
            setShowUpload(true);
            try {
              await runPhotoAnalysis(b64);
            } catch (e) {
              setAnalyzing(false);
              setShowUpload(false);
            }
          }}
          onRetake={() => {
            setQualityCheck(null);
            // Re-open the gallery picker for a fresh shot
            setTimeout(() => galleryInputRef.current?.click(), 200);
          }}
        />
        <FavoritesModal open={showFavorites} onOpenChange={setShowFavorites} favorites={favorites} loadingFavorites={loadingFavorites} addingFavorite={addingFavorite} onAddFavorite={addFavoriteAsMeal} onDeleteFavorite={deleteFavorite} onRenameFavorite={renameFavorite} />
        <BadgesModal open={showBadges} onOpenChange={setShowBadges} badges={badges} />
        <WorkoutModal
          open={showWorkout}
          onOpenChange={(o) => {
            setShowWorkout(o);
            if (o) {
              // Iter. 406.t — sync the in-modal date picker with the dashboard
              // "Rattrapage" date if the user already selected one; else default
              // to today so the date field is always populated.
              setWorkoutForm((prev) => ({
                ...prev,
                target_date: selectedDate || getLocalDateString(),
              }));
            }
          }}
          workoutForm={workoutForm}
          onFormChange={setWorkoutForm}
          onSubmit={logWorkout}
          loading={workoutLoading}
        />
        <LabelScannerModal open={showLabelScanner} onOpenChange={setShowLabelScanner} scanning={scanningLabel} labelResult={labelResult} labelProductName={labelProductName} onProductNameChange={setLabelProductName} labelServings={labelServings} onServingsChange={setLabelServings} onRescan={handleRescanLabel} onAdd={addLabelAsFood} />
        <BarcodeResultModal open={!!barcodeResult} onOpenChange={(open) => !open && handleCancelBarcode()} lookingUp={lookingUpBarcode} barcodeResult={barcodeResult} barcodeServings={barcodeServings} onServingsChange={setBarcodeServings} barcodeMealType={barcodeMealType} onMealTypeChange={setBarcodeMealType} onAdd={addBarcodeProductAsMeal} onCancel={handleCancelBarcode} adding={analyzing} onNutritionOverride={(newNutrition) => setBarcodeResult(prev => prev ? { ...prev, nutrition_per_100g: newNutrition, is_custom_override: true } : prev)} />

        {showBarcodeScanner && (
          <div className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center">
            <BarcodeScanner onScanSuccess={handleBarcodeScan} onScanError={(error) => { console.error('Barcode scan error:', error); toast.error(language === 'fr' ? 'Erreur de scan' : 'Scan error'); }} onClose={() => setShowBarcodeScanner(false)} language={language} />
          </div>
        )}

        {celebrationBadge && <BadgeCelebration badge={celebrationBadge} onClose={() => setCelebrationBadge(null)} language={language} />}
        <GeolocationPrompt onComplete={() => {}} />
        {RamadanModal}
        {FastingGuardModal}

        {/* DRAFT MEAL PANEL — Multi-ingredient (Iter. 405.f extracted) */}
        <DraftMealSheet
          open={showDraftMeal}
          onOpenChange={setShowDraftMeal}
          items={draftMealItems}
          saving={savingDraft}
          language={language}
          onRemoveItem={removeDraftItem}
          onScanAnother={scanAnotherIngredient}
          onSave={saveDraftMeal}
          onCancel={() => { setShowDraftMeal(false); clearDraftMeal(); }}
        />

        {/* J5 Workout Onboarding Wizard (Iter. 405.h) */}
        <WorkoutOnboardingModal
          open={showWorkoutOnboarding}
          onClose={() => setShowWorkoutOnboarding(false)}
          language={language}
          onSuccess={(planData) => {
            // Plan persisted by backend — navigate to /workouts where
            // the user can check off each set as they train. The
            // /workouts/active loader will pick up the brand-new plan.
            // Iter. 406.p — broadcast so ActivateCycleBanner refreshes
            // and hides itself now that an active cycle exists.
            try { window.dispatchEvent(new Event('yumix:onboarding-refresh')); } catch { /* ignore */ }
            try { navigate('/workouts'); } catch { /* fallback handled by toast */ }
          }}
          onSkipped={() => {
            // Welcome challenge has marked J5 as skipped — refresh the
            // widget so the user sees J6 unlocked.
            try { api.get('/welcome-challenge/status').catch(() => {}); } catch { /* ignore */ }
          }}
        />

        {/* Floating draft meal indicator */}
        {draftMealItems.length > 0 && !showDraftMeal && !showBarcodeScanner && !barcodeResult && !showLabelScanner && (
          <button
            onClick={() => setShowDraftMeal(true)}
            className="fixed bottom-24 left-4 right-4 z-50 bg-primary text-primary-foreground rounded-xl p-3 flex items-center justify-between shadow-lg animate-in slide-in-from-bottom"
            data-testid="draft-meal-banner"
          >
            <div className="flex items-center gap-2">
              <UtensilsCrossed className="w-4 h-4" />
              <span className="text-sm font-semibold">
                {draftMealItems.length} {language === 'fr' ? 'ingredient(s) en attente' : 'ingredient(s) pending'}
              </span>
            </div>
            <span className="text-xs bg-white/20 rounded-full px-2 py-0.5">
              {draftMealItems.reduce((s, i) => s + (i.calories || 0), 0)} cal
            </span>
          </button>
        )}
      </div>
      </PullToRefresh>
    </Layout>
  );
};

export default Dashboard;
